// ✅ อัปเดต: Dashboard.js แบบ Real-time ด้วย onSnapshot

import { useEffect, useState, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import ChatView from './components/ChatView';
import ChatListItem from './components/ChatListItem';
import ChatInput from './components/ChatInput';
import GptSuggest from './components/GptSuggest';
import SendMediaDrawer from './components/Refinery-Asset';

const firebaseConfig = {
  apiKey: "AIzaSyBALO-oNqvxmRhTSx9S54A5PIilTrxVOYM",
  authDomain: "refinery-desk-dashboard.firebaseapp.com",
  projectId: "refinery-desk-dashboard",
  storageBucket: "refinery-desk-dashboard.firebasestorage.app",
  messagingSenderId: "28035012838",
  appId: "1:28035012838:web:eb80f171a10371cdbec327"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

export default function Dashboard() {
  const [messages, setMessages] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [pendingText, setPendingText] = useState('');
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const chatListRef = useRef(null);
  const gptSuggestRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'messages'), orderBy('createdAt', 'desc'), limit(500));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const groupedByUser = {};
      data.forEach(msg => {
        const uid = msg.userId;
        if (!groupedByUser[uid]) {
          groupedByUser[uid] = msg;
        }
      });

      setGrouped(groupedByUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedUserId) return;

    setLoading(true);
    const q = query(
      collection(db, 'messages'),
      where('userId', '==', selectedUserId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMessages(data.reverse());
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedUserId]);

  const handleSendMessage = async (text) => {
    if (!selectedUserId) return;
    const latestCustomerInfo = grouped[selectedUserId] || {};
    await addDoc(collection(db, 'messages'), {
      from: 'me',
      role: 'me',
      type: 'text',     
      userId: selectedUserId,
      customer: text,
      createdAt: serverTimestamp(),
      displayName: latestCustomerInfo.displayName || 'Customer',
      pictureUrl: latestCustomerInfo.pictureUrl || '',
    });
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <div className="w-[320px] bg-white border-r flex flex-col h-full min-h-0">
          <div className="sticky top-0 z-30 bg-white border-b shadow-sm">
            <h1 className="text-xl font-bold p-4">Refinery Desk</h1>
          </div>
          <div className="flex-1 overflow-y-auto" ref={chatListRef}>
            {Object.entries(grouped).map(([userId, msg]) => (
              <ChatListItem
                key={userId}
                userId={userId}
                msg={msg}
                onSelect={setSelectedUserId}
                isActive={userId === selectedUserId}
              />
            ))}
          </div>
        </div>

        {/* ChatView + Input */}
        <div className="flex-1 flex flex-col min-h-0">
          {selectedUserId ? (
            loading ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                Loading chat...
              </div>
            ) : (
              <>
                <div className="sticky top-0 z-30 bg-white border-b shadow-sm p-4 flex justify-between items-center">
                  <h2 className="text-xl font-semibold">
                    Chat with {grouped[selectedUserId]?.displayName}
                  </h2>
                </div>

                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    <ChatView messages={messages} gptSuggestRef={gptSuggestRef} />
                  </div>

                  <div className="shrink-0 border-t bg-white p-2">
                    <ChatInput
                      onSend={handleSendMessage}
                      selectedUser={grouped[selectedUserId]}
                      text={pendingText}
                      setText={setPendingText}
                      onOpenDrawer={() => setDrawerOpen(true)}
                    />
                  </div>
                </div>
              </>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              Select a conversation
            </div>
          )}
        </div>
      </div>

      <div className="h-[40vh] shrink-0 border-t bg-white relative">
        <GptSuggest
          key={selectedUserId}
          ref={gptSuggestRef}
          selectedUserId={selectedUserId}
          selectedCustomer={grouped[selectedUserId]}
          onSelect={setPendingText}
          chatMessagesFromCustomer={messages}
        />

        {/* ✅ Overlay Drawer */}
        {isDrawerOpen && (
          <div className="absolute inset-0 z-50 bg-white shadow-xl rounded-t-2xl overflow-auto">
            <SendMediaDrawer
              onClose={() => setDrawerOpen(false)}
              selectedUserId={selectedUserId}
              selectedCustomer={grouped[selectedUserId]}
            />
          </div>
        )}
      </div>
    </div>
  );
}
